import {Component, OnInit, AfterViewInit, OnDestroy, ViewChildren, ElementRef} from '@angular/core';
import {CompanyService} from '../../../services/company.service';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup, FormControlName} from '@angular/forms';
import { ImageCroppedEvent } from 'ngx-image-cropper';

import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/observable/merge';
import {Observable} from 'rxjs/Observable';
import {Subscription} from 'rxjs/Subscription';
import {GenericValidator} from '../../../shared/generic-validator';
import {Title} from '@angular/platform-browser';
import {ICompany} from '../../../model/interfaces/company.interface';

@Component({
    selector: 'app-company-edit',
    templateUrl: './company-edit.component.html',
    styleUrls: ['./company-edit.component.scss'],
    providers: [CompanyService]
})
export class CompanyEditComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren(FormControlName, {read: ElementRef}) formInputElements: ElementRef[];

    errorMessage: string;
    companyForm: FormGroup;

    company: ICompany;
    private sub: Subscription;

    viewEmailScreenshot: boolean;
    showAPIPassword: boolean;

    // Use with the generic validation message class
    displayMessage: { [key: string]: string } = {};
    private readonly validationMessages: { [key: string]: { [key: string]: string } };
    private genericValidator: GenericValidator;

    imageChangedEvent: any = '';
    croppedImage = '';
    randInt = 1000;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private companyService: CompanyService,
        private title: Title
    ) {

        this.randInt = companyService.randInt;

        // Defines all of the validation messages for the form.
        // These could instead be retrieved from a file or database.
        this.validationMessages = {
            CO1_NAME: {
                required: 'customer name is required.',
                minlength: 'customer name must be at least three characters.',
                maxlength: 'customer name cannot exceed 50 characters.'
            },
        };

        // Define an instance of the validator for use with this form,
        // passing in this form's set of validation messages.
        this.genericValidator = new GenericValidator(this.validationMessages);

        this.title.setTitle('Admin - Companies');
    }

    ngOnInit(): void {
        this.companyForm = this.fb.group({
            CO1_SHOW_DEFAULT: [''],
            CO1_DOMAIN: [''],
            CO1_COMPANY_LOGO_NAME: [''],
            CO1_EMAIL_SENDER_NAME: [''],
            CO1_EMAIL_COMPANY_NAME: [''],
            CO1_PO_PDF_FOOTER: [''],
            CO1_API_URL: [''],
            CO1_API_USERNAME: [''],
            CO1_API_ACCESS_KEY: ['']
        });

        this.viewEmailScreenshot = false;
        this.showAPIPassword = false;

        // Read the customer Id from the route parameter
        this.sub = this.route.params.subscribe(
            params => {
                this.getCompany(params['id']);
            }
        );

    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    ngAfterViewInit(): void {
        // Watch for the blur event from any input element on the form.
        const controlBlurs: Observable<any>[] = this.formInputElements
            .map((formControl: ElementRef) => Observable.fromEvent(formControl.nativeElement, 'blur'));

        // Merge the blur event observable with the valueChanges observable
        Observable.merge(this.companyForm.valueChanges, ...controlBlurs).debounceTime(800).subscribe(value => {
            this.displayMessage = this.genericValidator.processMessages(this.companyForm);
        });
    }

    getCompany(id: number): void {
        this.companyService.getById(id)
            .subscribe(
                (company: ICompany) => this.onCompanyRetrieved(company),
                (error: any) => this.errorMessage = <any>error
            );
    }

    onCompanyRetrieved(company: ICompany): void {
        if (this.companyForm) {
            this.companyForm.reset();
        }

        this.company = company[0] || company;

        if (company.CO1_LOGO_FILEPATH) {
            this.croppedImage = company.CO1_LOGO_FILEPATH + '?' + this.randInt;
        }

        // Update the data on the form
        this.companyForm.patchValue({
            CO1_ID: this.company.CO1_ID,
            CO1_SHOW_DEFAULT: this.company.CO1_SHOW_DEFAULT === 'X' ? 1 : 0,
            CO1_DOMAIN: this.company.CO1_DOMAIN,
            CO1_COMPANY_LOGO_NAME: this.company.CO1_COMPANY_LOGO_NAME,
            CO1_EMAIL_SENDER_NAME: this.company.CO1_EMAIL_SENDER_NAME,
            CO1_EMAIL_COMPANY_NAME: this.company.CO1_EMAIL_COMPANY_NAME,
            CO1_PO_PDF_FOOTER: this.company.CO1_PO_PDF_FOOTER,
            CO1_API_URL: this.company.CO1_API_URL,
            CO1_API_USERNAME: this.company.CO1_API_USERNAME,
            CO1_API_ACCESS_KEY: this.company.CO1_API_ACCESS_KEY
        });

    }

    onCancel() {
        this.router.navigate(['companies']);
    }

    onShowEmailScreenshot() {
        this.viewEmailScreenshot = true;
    }

    saveCompany(): void {
        if (this.companyForm.dirty && this.companyForm.valid) {
            // Copy the form values over the customer object values
            const c = Object.assign({}, this.company, this.companyForm.value, {
                image: this.croppedImage ? this.croppedImage : ''
            });

            this.companyService.save(c, this.company.CO1_ID === 0)
                .subscribe(
                    () => this.onSaveComplete(),
                    (error: any) => this.errorMessage = <any>error
                );
        } else if (!this.companyForm.dirty) {
            this.onSaveComplete();
        }
    }

    onSaveComplete(): void {
        // Reset the form to clear the flags
        this.companyForm.reset();
        this.router.navigate(['companies']);
    }

    toggleAPIPassword(): void {
        this.showAPIPassword = !this.showAPIPassword;
    }

    fileChangeEvent(event: any): void {
        this.imageChangedEvent = event;
    }
    imageCropped(event: ImageCroppedEvent) {
        this.companyForm.markAsDirty();
        this.croppedImage = event.base64;
    }
    imageLoaded() {
        // show cropper
    }
    cropperReady() {
        // cropper ready
    }
    loadImageFailed() {
        // show message
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/companies/company-edit/company-edit.component.ts